import { AwardsWall } from "@/components/AwardsWall";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Plane, Palmtree, FileCheck, Ship, BedDouble, Car,
  Building2, Landmark, Users, Globe2, MapPin, ArrowRight, Check,
} from "lucide-react";
import { Header } from "@/components/sections/Navbar";
import { Footer } from "@/components/sections/Footer";
import { FloatingWidgets } from "@/components/sections/FloatingWhatsApp";
import { AssociationsMarquee } from "@/components/sections/AssociationsMarquee";
import { SmartImage } from "@/components/SmartImage";
import { BRAND } from "@/data/content";
import { FounderSection, RecognitionSection, OfficeGallery } from "@/components/about/AboutSections";
import { DESTINATIONS_COUNT, COUNTRIES_COUNT, YEARS_OF_TRUST } from "@/lib/siteStats";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] } }),
};

const EXPERTISE = [
  { icon: Plane, title: "Air Tickets", desc: "Domestic & international flights at the best available fares." },
  { icon: Palmtree, title: "Holiday Packages", desc: `Curated group tours & bespoke holidays across ${COUNTRIES_COUNT} countries.` },
  { icon: FileCheck, title: "Visa Services", desc: "End-to-end assistance & documentation for 50+ countries." },
  { icon: Ship, title: "Cruise Bookings", desc: "Luxury ocean & river cruises with the world's finest lines." },
  { icon: BedDouble, title: "Hotel Reservations", desc: "Handpicked stays from boutique gems to five-star icons." },
  { icon: Car, title: "Cabs & Transfers", desc: "Chauffeur-driven transfers and self-drive holidays." },
];

const CLIENTELE = [
  { icon: Building2, title: "Corporations", desc: "MICE, off-sites & incentive travel." },
  { icon: Landmark, title: "Governments & PSUs", desc: "Compliant, large-scale travel management." },
  { icon: Users, title: "Group Travel", desc: "Families, communities & special-interest groups." },
  { icon: Globe2, title: "Global Travellers", desc: "NRI & international guests from around the world." },
];

const OFFICES = [
  { city: "Gorakhpur", tag: "Head Office", flag: "in" },
  { city: "Lucknow", tag: "India", flag: "in" },
  { city: "Delhi", tag: "India", flag: "in" },
  { city: "Dubai", tag: "UAE", flag: "ae" },
  { city: "London", tag: "United Kingdom", flag: "gb" },
  { city: "Bangkok", tag: "Thailand", flag: "th" },
  { city: "Jeddah", tag: "Saudi Arabia", flag: "sa" },
];

const STATS = [
  { value: `${YEARS_OF_TRUST}+`, label: "Years of Trust" },
  { value: `${DESTINATIONS_COUNT}`, label: "Destinations" },
  { value: "1,25,000+", label: "Happy Travellers" },
  { value: "7", label: "Global Offices" },
];

const CHAPTERS = [
  { n: "01", t: "Founded on trust", d: `Established in ${BRAND.established}, ${BRAND.shortName} began with a single promise — to make every journey feel effortless and extraordinary.` },
  { n: "02", t: "Curated, never templated", d: "Every itinerary is designed by senior curators who have walked the routes themselves — no copy-paste holidays." },
  { n: "03", t: "Global reach, local care", d: "From Gorakhpur to Dubai and London, our on-ground teams look after you at every step of the way." },
];

export default function AboutUs() {
  return (
    <main data-testid="about-page" className="bg-white text-[var(--navy)] overflow-x-hidden">
      <Header />

      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden" data-testid="about-hero-section">
        <motion.div initial={{ scale: 1.12 }} animate={{ scale: 1 }} transition={{ duration: 6, ease: "easeOut" }} className="absolute inset-0">
          <SmartImage src="/assets/media/company/office-cabin.jpg" alt="Royal Tours Group Head Office, Gorakhpur" className="w-full h-full object-cover" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#0b2545]/95 via-[#0b2545]/80 to-[#0b2545]/30" />
        <div className="relative max-w-[1280px] mx-auto px-4 lg:px-6 py-24 text-white">
          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={0} className="text-[var(--yellow)] font-semibold text-[11px] tracking-[0.35em] mb-4">
            ABOUT {BRAND.shortName.toUpperCase()}
          </motion.div>
          <motion.h1 variants={fadeUp} initial="hidden" animate="show" custom={1} className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl">
            Where Luxury <span className="text-[var(--yellow)]">Meets Journey</span>
          </motion.h1>
          <motion.p variants={fadeUp} initial="hidden" animate="show" custom={2} className="mt-5 text-white/85 max-w-xl text-base md:text-lg">
            A premium travel house from Gorakhpur with a truly global presence — crafting unforgettable holidays since {BRAND.established}.
          </motion.p>
        </div>
      </section>

      <FounderSection />
      <RecognitionSection />

      {/* Story */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-[1280px] mx-auto px-4 lg:px-6 grid lg:grid-cols-12 gap-12 items-center">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="lg:col-span-5 relative">
            <div className="rounded-2xl overflow-hidden aspect-[4/5] shadow-2xl ring-1 ring-black/5">
              <SmartImage src="/assets/media/company/award-trophy.jpg" alt="Maaz Ahmad with the India Travel Awards 2026 trophy" className="w-full h-full object-cover object-top" />
            </div>
            <div className="absolute -bottom-6 -right-4 bg-[var(--navy)] text-white rounded-xl px-6 py-4 shadow-xl hidden md:block">
              <div className="font-serif-display text-3xl text-[var(--yellow)]">Est. 2010</div>
              <div className="text-[11px] tracking-widest uppercase text-white/70">A legacy of trust</div>
            </div>
          </motion.div>
          <div className="lg:col-span-7">
            <div className="text-[var(--red)] font-semibold text-[11px] tracking-[0.25em] mb-2">OUR STORY</div>
            <h2 className="font-serif-display text-3xl md:text-4xl leading-tight">
              A dedication to <span className="text-[var(--yellow-2)]">luxury travel.</span>
            </h2>
            <div className="mt-8 space-y-8">
              {CHAPTERS.map((c, i) => (
                <motion.div key={c.n} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} className="flex gap-5">
                  <div className="font-serif-display text-2xl text-[var(--yellow-2)] w-12 shrink-0">{c.n}</div>
                  <div>
                    <div className="font-semibold text-lg">{c.t}</div>
                    <p className="text-gray-600 mt-1 text-sm md:text-base leading-relaxed">{c.d}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Expertise */}
      <section className="py-16 md:py-20 bg-[var(--soft)] border-y border-gray-200">
        <div className="max-w-[1280px] mx-auto px-4 lg:px-6">
          <div className="max-w-2xl">
            <div className="text-[var(--red)] font-semibold text-[11px] tracking-[0.25em] mb-2">OUR EXPERTISE</div>
            <h2 className="font-serif-display text-3xl md:text-4xl leading-tight">
              Everything your journey needs, <span className="text-[var(--yellow-2)]">under one roof.</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
            {EXPERTISE.map((e, i) => (
              <motion.div key={e.title} data-testid={`expertise-${i}`} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }}
                className="group bg-white rounded-xl p-6 border border-gray-200 hover:border-[var(--yellow-2)] hover:shadow-xl transition-all">
                <div className="w-12 h-12 rounded-lg bg-[var(--yellow)] flex items-center justify-center group-hover:scale-110 transition-transform">
                  <e.icon className="w-6 h-6 text-[var(--navy)]" strokeWidth={1.7} />
                </div>
                <div className="font-semibold text-lg mt-4">{e.title}</div>
                <p className="text-gray-600 text-sm mt-1.5 leading-relaxed">{e.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <OfficeGallery />

      {/* Stats */}
      <section className="py-16 bg-[var(--navy)] text-white" data-testid="about-stats-bar">
        <div className="max-w-[1280px] mx-auto px-4 lg:px-6 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {STATS.map((s, i) => (
            <motion.div key={s.label} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} className="text-center" data-testid={`stat-${i}`}>
              <div className="font-serif-display text-4xl md:text-5xl text-[var(--yellow)]">{s.value}</div>
              <div className="text-white/70 text-xs md:text-sm tracking-widest uppercase mt-2">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Clientele */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-[1280px] mx-auto px-4 lg:px-6">
          <div className="max-w-2xl">
            <div className="text-[var(--red)] font-semibold text-[11px] tracking-[0.25em] mb-2">OUR CLIENTELE</div>
            <h2 className="font-serif-display text-3xl md:text-4xl leading-tight">
              Trusted by those who <span className="text-[var(--yellow-2)]">expect the best.</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
            {CLIENTELE.map((c, i) => (
              <motion.div key={c.title} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} className="rounded-xl p-6 bg-[var(--soft)] border border-gray-200">
                <c.icon className="w-8 h-8 text-[var(--yellow-2)]" strokeWidth={1.6} />
                <div className="font-semibold text-lg mt-4">{c.title}</div>
                <p className="text-gray-600 text-sm mt-1.5">{c.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Global presence */}
      <section className="py-16 md:py-20 bg-[var(--soft)] border-y border-gray-200">
        <div className="max-w-[1280px] mx-auto px-4 lg:px-6">
          <div className="max-w-2xl">
            <div className="text-[var(--red)] font-semibold text-[11px] tracking-[0.25em] mb-2">GLOBAL PRESENCE</div>
            <h2 className="font-serif-display text-3xl md:text-4xl leading-tight">
              One family, <span className="text-[var(--yellow-2)]">seven cities worldwide.</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
            {OFFICES.map((o, i) => (
              <motion.div key={o.city} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} data-testid={`office-${i}`}
                className={`rounded-xl p-5 border ${o.tag === "Head Office" ? "bg-[var(--navy)] text-white border-[var(--navy)]" : "bg-white border-gray-200"}`}>
                <div className="flex items-center gap-2">
                  <img src={`https://flagcdn.com/h20/${o.flag}.png`} alt="" className="rounded-sm shadow" width="20" height="14" />
                  <MapPin className={`w-4 h-4 ${o.tag === "Head Office" ? "text-[var(--yellow)]" : "text-[var(--yellow-2)]"}`} />
                </div>
                <div className="font-serif-display text-2xl mt-3">{o.city}</div>
                <div className={`text-[11px] tracking-widest uppercase mt-1 ${o.tag === "Head Office" ? "text-[var(--yellow)]" : "text-gray-500"}`}>{o.tag}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Affiliations */}
      <AssociationsMarquee />

      {/* CTA */}
      <section className="py-20 bg-[var(--navy)] text-white text-center">
        <div className="max-w-3xl mx-auto px-4 lg:px-6">
          <h2 className="font-serif-display text-3xl md:text-5xl leading-tight">
            Ready to travel <span className="text-[var(--yellow)]">the Royal way?</span>
          </h2>
          <p className="text-white/75 mt-4 max-w-xl mx-auto">
            Let a senior curator design a journey around your dreams — no obligations, no templates.
          </p>
          <Link to="/contact" data-testid="about-cta-plan" className="mt-8 inline-flex items-center gap-2 bg-[var(--yellow)] hover:bg-white text-[var(--navy)] font-semibold px-8 py-3.5 rounded-full transition-colors">
            Plan Your Luxury Journey <ArrowRight className="w-4 h-4" />
          </Link>
          <div className="mt-6 flex items-center justify-center gap-2 text-white/60 text-sm">
            <Check className="w-4 h-4 text-[var(--yellow)]" /> IATA accredited · {YEARS_OF_TRUST}+ years · {COUNTRIES_COUNT} countries
          </div>
        </div>
      </section>

      <AwardsWall />
      <Footer />
      <FloatingWidgets />
    </main>
  );
}
